export type Category = {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  accentTag: string;
};

export type Article = {
  id: string;
  slug: string;
  title: string;
  categoryId: string;
  excerpt: string;
  cover: string;
  tags: string[];
  heat: number;
  publishedAt: string;
  author: string;
  readTime: number;
  content: { heading: string; paragraphs: string[] }[];
};

export const categories: Category[] = [
  {
    id: "cat-variety",
    name: "综艺现场",
    slug: "variety",
    description: "聚焦正在播出的综艺节目、赛制改版与嘉宾化学反应，拆解一档节目为什么好看。",
    icon: "ri-tv-2-line",
    accentTag: "热播中",
  },
  {
    id: "cat-drama",
    name: "影视风向",
    slug: "drama",
    description: "剧集与电影的档期热度、类型趋势和口碑走向，记录观众口味正在发生的变化。",
    icon: "ri-film-line",
    accentTag: "追剧中",
  },
  {
    id: "cat-music",
    name: "音乐现场",
    slug: "music",
    description: "音乐节、演唱会与音乐综艺的现场观察，关注舞台之外的真实体验。",
    icon: "ri-music-2-line",
    accentTag: "现场声",
  },
  {
    id: "cat-web",
    name: "网络话题",
    slug: "web",
    description: "拆解全网热议话题的传播链路，帮你在围观之前先看清楚来龙去脉。",
    icon: "ri-fire-line",
    accentTag: "高热",
  },
  {
    id: "cat-fashion",
    name: "时尚潮流",
    slug: "fashion",
    description: "从街拍到通勤，从国风到实用主义，记录当下正在发生的审美变化。",
    icon: "ri-t-shirt-line",
    accentTag: "衣橱",
  },
  {
    id: "cat-game",
    name: "赛事娱乐",
    slug: "game",
    description: "体育赛事与运动综艺的交叉地带，关注竞技表达和娱乐包装之间的平衡。",
    icon: "ri-trophy-line",
    accentTag: "竞技场",
  },
];

export const articles: Article[] = [
  {
    id: "a01",
    slug: "chase-light-night-run-format",
    title: "《追光夜行》赛制改版：为什么观众开始为“慢下来”买单",
    categoryId: "cat-variety",
    excerpt:
      "把淘汰节奏拉长、把个人故事讲透，《追光夜行》第三季的改版让讨论点从输赢转向了过程。我们复盘了这档节目做对的几件事。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20warm%20vermilion%20red%20and%20amber%20gold%20geometric%20shapes%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20circles%20arcs%20and%20diagonal%20ribbons%20suggesting%20a%20stage%20spotlight%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-01&orientation=landscape",
    tags: ["综艺", "赛制", "口碑"],
    heat: 9820,
    publishedAt: "2026-09-11",
    author: "编辑部 · 沈晚",
    readTime: 6,
    content: [
      {
        heading: "改版之后，第一个变化是“不着急”",
        paragraphs: [
          "第三季开播前，节目组放出的一句预告是“这一季我们慢一点”。很多人当时以为只是宣传话术，但前四期看下来，节奏确实变了：单期不再强行淘汰两人，而是把评分的权重拆成现场表现、团队协作和成长曲线三部分。",
          "节奏变慢之后，观众讨论的内容也从“谁该走”转向了“这段为什么打动我”。社交平台上围绕单期舞台的长评明显比上一季更多，有观众把这种变化形容为“终于有时间看清楚一个人在做什么”。",
        ],
      },
      {
        heading: "赛制是骨架，人物关系才是血肉",
        paragraphs: [
          "改版最聪明的地方，是没有把注意力全放在规则上，而是用规则给人物关系留出空间。团队赛提前到中期，让原本互不熟悉的选手有了一段共同完成任务的经历，这段经历比任何旁白都更能解释他们后来为什么互相帮衬。",
          "有编导在采访中提到，他们删掉了大量“解释性字幕”，改成让镜头自己说话。这个取舍有风险，但结果是节目少了几分被安排的痕迹，多了几分现场感。",
        ],
      },
      {
        heading: "“慢下来”能不能被复制",
        paragraphs: [
          "并不是所有综艺都适合拉长节奏。慢的前提是内容密度足够，否则就只是拖沓。这档节目能成立，是因为它本身就有足够多的舞台和练习过程可以呈现。",
          "对观众来说，值得记住的或许不是哪条赛制，而是它证明了一件事：在信息过载的时候，愿意把一件事讲清楚的节目，反而更容易被留下来。",
        ],
      },
    ],
  },
  {
    id: "a02",
    slug: "slow-variety-return-logic",
    title: "一档慢综艺的翻红逻辑：内容留白正在取代密集剪辑",
    categoryId: "cat-variety",
    excerpt:
      "曾经被嫌弃“太安静”的生活类慢综艺，最近半年在二次剪辑和长视频平台重新收获关注。留白为什么突然值钱了？",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20calm%20sage%20green%20and%20amber%20gold%20geometric%20shapes%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20soft%20arcs%20and%20horizontal%20bands%20suggesting%20quiet%20countryside%20rhythm%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-02&orientation=landscape",
    tags: ["慢综艺", "剪辑", "二次传播"],
    heat: 7640,
    publishedAt: "2026-09-10",
    author: "编辑部 · 林知",
    readTime: 5,
    content: [
      {
        heading: "被剪短的节目，为什么反而需要长镜头",
        paragraphs: [
          "过去几年，综艺剪辑普遍追求信息密度：三秒一个笑点，五秒一个转折。但观众在短视频里已经习惯了这种节奏，回到长视频时反而觉得喘不过气。",
          "慢综艺的翻红恰好出现在这个节点。它把镜头停下来的时间还给了观众，让“什么都没发生”的片段也有了被观看的价值。",
        ],
      },
      {
        heading: "留白不是空白，是给情绪留位置",
        paragraphs: [
          "真正好看的慢综艺，安静的部分往往带着信息：一顿饭怎么吃完、一段路怎么走完，都是关系的注脚。观众记住的通常不是台词，而是某个人发呆的两秒钟。",
          "这也是二次剪辑喜欢这类内容的原因——可拆解的细节多，每一小段都能独立成立。",
        ],
      },
      {
        heading: "密集与留白，其实是一道配比题",
        paragraphs: [
          "留白并不等于放弃节奏。成熟的节目会在平稳的底色上安排几个集中的高潮，让情绪有起伏而不是一直平淡。",
          "对创作者而言，真正的考验不是敢不敢慢，而是慢下来之后，画面里到底还有没有东西可看。",
        ],
      },
    ],
  },
  {
    id: "a03",
    slug: "short-series-suspense-trend",
    title: "悬疑剧进入“短剧集”时代：12集讲完一个案子的得与失",
    categoryId: "cat-drama",
    excerpt:
      "近一年上线的悬疑剧里，12集以内的作品占了明显更高的比例。短，是效率，也是一场对编剧功力的公开考试。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20deep%20ink%20charcoal%20with%20vermilion%20red%20accents%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20sharp%20triangles%20and%20concentric%20circles%20suggesting%20mystery%20and%20clues%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-03&orientation=landscape",
    tags: ["悬疑", "短剧集", "剧本"],
    heat: 8890,
    publishedAt: "2026-09-09",
    author: "编辑部 · 周叙",
    readTime: 7,
    content: [
      {
        heading: "短剧集的优势：没有时间注水",
        paragraphs: [
          "12集的容量意味着编剧必须在前两集就把主线立住，没有太多空间用来铺设无关支线。对观众来说，最直接的感受是“舍不得倍速”。",
          "平台侧的逻辑也清晰：更短的剧集意味着更快的排播周期和更低的试错成本，一旦口碑起来，能在很短时间内完成一轮完整传播。",
        ],
      },
      {
        heading: "代价是容错率变低",
        paragraphs: [
          "短剧集最大的风险在于人物来不及立起来。如果前三集都在推案情而没有让观众真正认识主角，后面再多的反转都很难产生代入感。",
          "部分作品的问题恰恰出在这里：案子讲得漂亮，人却像工具。观众记住了谜题，却没记住解开谜题的人。",
        ],
      },
      {
        heading: "观众在意的其实不是集数",
        paragraphs: [
          "把悬疑剧的热度简单归结为“集数变少”并不准确。观众真正在意的是每一集有没有推进，而不是总共有多少集。",
          "短剧集只是把这个问题放大了：好则紧凑，差则仓促。它筛掉的是那些靠拖泥带水撑时长的作品。",
        ],
      },
    ],
  },
  {
    id: "a04",
    slug: "summer-slot-realist-drama",
    title: "暑期档热度复盘：小成本现实题材如何拿下口碑第一",
    categoryId: "cat-drama",
    excerpt:
      "没有大制作、没有强阵容，一部聚焦普通职业的现实题材剧集靠什么在暑期档的口碑榜上站稳了前排？",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20warm%20amber%20and%20soft%20olive%20geometric%20shapes%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20rectangles%20and%20horizontal%20ribbons%20suggesting%20city%20life%20and%20daily%20work%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-04&orientation=landscape",
    tags: ["现实题材", "口碑", "暑期档"],
    heat: 6180,
    publishedAt: "2026-09-08",
    author: "编辑部 · 何一",
    readTime: 6,
    content: [
      {
        heading: "它赢在了“具体”",
        paragraphs: [
          "这部剧最常被提到的优点是细节扎实：办公室的会议流程、通勤路上的早晚高峰、加班时点的那份外卖，都不是为了好看而摆放的道具。",
          "现实题材最大的挑战是让观众相信，而相信的前提是具体。越具体的场景，越容易让人把自己的经验投射进去。",
        ],
      },
      {
        heading: "选角思路值得一说",
        paragraphs: [
          "剧里几乎没有流量标签，主演大多是话剧和配角出身的演员。这个选择牺牲了开播时的曝光度，却换来了更稳定的表演质感。",
          "中后段几场长对话戏被大量讨论，恰好说明当演员能撑住情绪时，观众愿意为慢下来的对话停留。",
        ],
      },
      {
        heading: "口碑和热度并不总是同一件事",
        paragraphs: [
          "它的播放量不是档期最高，但完播率和长评比例明显靠前。对平台而言，这类作品的价值在于留住真正在看内容的人。",
          "暑期档之后，现实题材的立项明显变多。跟风未必是坏事，但能不能复制它的“具体”，才是关键。",
        ],
      },
    ],
  },
  {
    id: "a05",
    slug: "music-festival-live-third-year",
    title: "音乐节回归线下第三年：观众到底在为什么买单",
    categoryId: "cat-music",
    excerpt:
      "票价、阵容、场地体验三件事，正在重新定义一场音乐节值不值得去。我们跟十几位常客聊了聊他们的真实判断标准。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20vivid%20vermilion%20red%20and%20amber%20gold%20shapes%2C%20deep%20cream%20paper%20background%2C%20layered%20collage%20of%20soundwave%20arcs%20and%20confetti%20dots%20suggesting%20outdoor%20music%20festival%20energy%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-05&orientation=landscape",
    tags: ["音乐节", "票价", "现场体验"],
    heat: 8430,
    publishedAt: "2026-09-07",
    author: "编辑部 · 苏杭",
    readTime: 6,
    content: [
      {
        heading: "阵容不再是唯一决策项",
        paragraphs: [
          "在三年前的购票逻辑里，压轴歌手几乎决定一切。但现在有更多观众把“场地动线”“退场交通”“现场音响”放进了选择清单。",
          "一位连着去了四年音乐节的乐迷说，她现在会先看往年同场地的返图，“如果散场要堵三个小时，我宁愿换个周末去别的城市看同一批乐队”。",
        ],
      },
      {
        heading: "体验溢价正在被接受",
        paragraphs: [
          "分区票、提前入场、专属休息区这些曾经被吐槽“太商业化”的设置，如今有了稳定的购买人群。观众愿意为确定性付费：确定能看清舞台，确定不用排两小时队。",
          "这并不矛盾。音乐节是少数人同时想要的场景，体验的差异化几乎是必然结果。",
        ],
      },
      {
        heading: "真正留人的还是音乐本身",
        paragraphs: [
          "所有服务升级最终都要回到内容。观众可以接受排队和暴晒，但很难接受压轴二十分钟的敷衍。",
          "从这个角度看，音乐节市场的成熟，其实是把关注点从“谁来”慢慢挪回到“唱得怎么样”。",
        ],
      },
    ],
  },
  {
    id: "a06",
    slug: "music-show-original-standard",
    title: "从翻唱到原创：音综舞台正在悄悄改变评价标准",
    categoryId: "cat-music",
    excerpt:
      "翻唱依旧好听，但越来越多的音乐综艺把重心放到了原创曲目。评价体系变了，选手的准备方式也随之改变。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20amber%20gold%20and%20deep%20ink%20geometric%20shapes%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20vertical%20bars%20and%20flowing%20curves%20suggesting%20music%20notation%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-06&orientation=landscape",
    tags: ["原创", "音综", "舞台"],
    heat: 5720,
    publishedAt: "2026-09-06",
    author: "编辑部 · 沈晚",
    readTime: 5,
    content: [
      {
        heading: "赛制变了，准备的周期也变了",
        paragraphs: [
          "当节目要求每位选手至少带来一首原创，备赛周期就从“练歌”变成了“写歌加改歌”。有选手提到，他这季有大半时间花在了和编曲老师反复推敲结构上。",
          "这直接影响舞台呈现：原创作品往往没有观众熟悉的旋律，前三十秒能不能抓住人，成了新的分水岭。",
        ],
      },
      {
        heading: "翻唱依然有市场，但边际收益在下降",
        paragraphs: [
          "翻唱的优势是自带熟悉度，但劣势同样明显——观众的心里已经有一个版本，超越它很难。",
          "近几季出圈度最高的舞台里，原创作品的比例在上升。这不是翻唱不好，而是原创更容易被记住“是谁唱的”。",
        ],
      },
      {
        heading: "对观众来说，这是一件好事",
        paragraphs: [
          "评价标准向原创倾斜，意味着节目不再只考验唱功，也开始考验表达。观众的收获是听到了更多没听过的歌。",
          "当然，原创不等于免检。真正被反复播放的，仍然是那些完成度足够高的作品。",
        ],
      },
    ],
  },
  {
    id: "a07",
    slug: "rational-spectating-consensus",
    title: "“吃瓜”也有边界：理性围观正在成为新的网络共识",
    categoryId: "cat-web",
    excerpt:
      "全网热点每天都在更新，但围观的方式正在发生变化。越来越多的人开始在意：这个话题值得怎么看、值不值得转发。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20sage%20green%20and%20vermilion%20red%20shapes%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20concentric%20rings%20and%20scattered%20dots%20suggesting%20online%20discussion%20and%20ripples%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-07&orientation=landscape",
    tags: ["网络话题", "理性围观", "传播"],
    heat: 9310,
    publishedAt: "2026-09-05",
    author: "编辑部 · 林知",
    readTime: 6,
    content: [
      {
        heading: "先看来源，再看内容",
        paragraphs: [
          "一个话题登上热搜之后，最先流出的一般不是完整信息，而是最容易被转发的片段。把来源和片段分开看，是现在很多人的第一反应。",
          "评论区里出现频率最高的一句话，从“真的假的”变成了“等一个更完整的说法”。这种等待本身，就是围观方式的变化。",
        ],
      },
      {
        heading: "转发之前，多问一句“然后呢”",
        paragraphs: [
          "情绪最容易在传播的第三四手达到顶峰。到那个时候，事实往往已经被压缩成一句话，而那句话通常是最极端的版本。",
          "不少平台开始给高热话题打上“信息持续更新中”的提示，提醒用户结论可能还在变化。这个设计不复杂，但有效。",
        ],
      },
      {
        heading: "围观是一种参与，也是一种责任",
        paragraphs: [
          "围观不等于冷漠，正因为每一次转发都在影响话题走向，所以才更需要克制。把不确定的地方说成不确定，是一种更有价值的表达。",
          "越热闹的话题，越值得慢一点。这不是扫兴，而是让讨论真正回到事情本身。",
        ],
      },
    ],
  },
  {
    id: "a08",
    slug: "trending-topic-marketing-logic",
    title: "热搜体质是怎么炼成的：话题营销的三条底层逻辑",
    categoryId: "cat-web",
    excerpt:
      "有些话题几乎不用推就能火，有些砸了预算也无人问津。差别往往不在投入，而在话题有没有“可参与”的接口。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20high%20contrast%20vermilion%20red%20and%20amber%20gold%20shapes%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20ascending%20bars%20and%20arrows%20suggesting%20trending%20charts%20and%20momentum%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-08&orientation=landscape",
    tags: ["热搜", "话题", "传播链路"],
    heat: 7050,
    publishedAt: "2026-09-04",
    author: "编辑部 · 周叙",
    readTime: 7,
    content: [
      {
        heading: "第一，话题要能被“接着说”",
        paragraphs: [
          "容易传播的话题往往有一个开放的结构：它是观点而不是结论，是问题而不是答案。观众看完之后有话说，才会产生二次传播。",
          "反过来，一个信息量很大的话题，如果观众看完只能点头，参与度通常不会高。",
        ],
      },
      {
        heading: "第二，情绪要找对出口",
        paragraphs: [
          "高热话题背后几乎总有一种明确的情绪：好胜、共鸣、好奇或者释然。情绪不需要很强烈，但必须清晰，否则它无法被快速识别和归类。",
          "营销做得好的人，不是制造情绪，而是找到那个已经被多数人压抑着的情绪，然后给它一个体面的出口。",
        ],
      },
      {
        heading: "第三，节奏比音量重要",
        paragraphs: [
          "话题的扩散通常分三段：起量、发酵、回落。强行延长发酵期往往适得其反，容易让观众产生审美疲劳。",
          "成熟的策略是接受回落，用下一个话题接上，而不是死守一个已经走完生命周期的话题。",
        ],
      },
    ],
  },
  {
    id: "a09",
    slug: "commute-fashion-utility",
    title: "通勤穿搭回潮：实用主义正在接管年轻人的衣柜",
    categoryId: "cat-fashion",
    excerpt:
      "从追求存在感到追求耐穿，年轻人的穿衣逻辑正在悄悄调头。这背后不只是预算问题，也是一种更松弛的生活态度。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20soft%20sage%20green%20and%20warm%20amber%20shapes%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20folded%20fabric%20bands%20and%20rounded%20rectangles%20suggesting%20minimal%20wardrobe%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-09&orientation=landscape",
    tags: ["穿搭", "实用主义", "通勤"],
    heat: 4890,
    publishedAt: "2026-09-03",
    author: "编辑部 · 何一",
    readTime: 5,
    content: [
      {
        heading: "“一件顶三件”重新成为卖点",
        paragraphs: [
          "买衣服时被问得最多的问题，从“拍照好不好看”变成了“能不能配我现有的裤子”。耐穿、好搭、易打理，重新回到了决策清单的前面。",
          "这不是消费降级，而是决策标准的变化：花同样的钱，希望得到更长的使用周期。",
        ],
      },
      {
        heading: "颜色收敛，版型放松",
        paragraphs: [
          "本季通勤场景里，低饱和的燕麦、灰绿和砖红占了主流。它们的好处是不挑场合，早晚通勤和周末出门都能穿。",
          "版型上，宽松但不拖沓的剪裁更受欢迎。合身不再等于贴身，留出一点空间反而更显精神。",
        ],
      },
      {
        heading: "松弛感是一种生活选择",
        paragraphs: [
          "穿搭风格的转向，往往对应着生活节奏的变化。当通勤时间变长、工作节奏变快，衣服首先要让人舒服。",
          "实用主义和好看从来不是对立面。真正的松弛感，是不再为了一件衣服消耗过多的注意力。",
        ],
      },
    ],
  },
  {
    id: "a10",
    slug: "guofeng-street-translation",
    title: "国风元素走上街头：不是复古，是重新翻译",
    categoryId: "cat-fashion",
    excerpt:
      "盘扣、立领、草木染出现在日常穿搭里已经不是新鲜事。这一轮国风的不同之处，在于它更像一种材料实验。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20deep%20vermilion%20red%20and%20muted%20sage%20green%20shapes%2C%20warm%20cream%20paper%20background%2C%20layered%20collage%20of%20rounded%20seals%20and%20silk%20ribbon%20bands%20suggesting%20traditional%20craft%20reinterpreted%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-10&orientation=landscape",
    tags: ["国风", "材料", "审美"],
    heat: 5230,
    publishedAt: "2026-09-02",
    author: "编辑部 · 苏杭",
    readTime: 5,
    content: [
      {
        heading: "从“像不像”到“好不好用”",
        paragraphs: [
          "早期国风设计容易被质疑“不够正宗”，这一轮讨论的焦点变成了“穿着舒服吗”。评价标准的转移，本身就说明它已经进入日常。",
          "很多设计师不再照搬传统形制，而是把面料、配色和局部结构拆出来，重新组合到现代版型上。",
        ],
      },
      {
        heading: "细节比轮廓更重要",
        paragraphs: [
          "一条改良的立领、一段手工的滚边，往往比整套复刻更有说服力。它们不喧宾夺主，却能让一件衣服立刻区别于普通基础款。",
          "这也是国风元素能进入通勤场景的原因：它可以只占很小的一部分。",
        ],
      },
      {
        heading: "重新翻译，需要耐心",
        paragraphs: [
          "把传统元素搬进日常，最难的不是设计，而是供应链。手工工艺量小、周期长，和快节奏的上新逻辑天然冲突。",
          "愿意为这部分耐心买单的品牌，最终得到的是更难被复制的东西。",
        ],
      },
    ],
  },
  {
    id: "a11",
    slug: "sports-variety-reality-balance",
    title: "赛事与娱乐的边界：运动综艺为什么越来越“真”",
    categoryId: "cat-game",
    excerpt:
      "假比赛、真综艺的模式正在被观众抛弃。当竞技强度提上来，运动类节目的看点也发生了变化。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20dynamic%20vermilion%20red%20and%20amber%20gold%20shapes%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20diagonal%20streaks%20and%20circular%20badges%20suggesting%20competition%20and%20speed%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-11&orientation=landscape",
    tags: ["运动综艺", "竞技", "真实性"],
    heat: 6410,
    publishedAt: "2026-09-01",
    author: "编辑部 · 林知",
    readTime: 6,
    content: [
      {
        heading: "观众一眼就能看出的“假”",
        paragraphs: [
          "运动类节目最怕的不是强度不够，而是结果被安排得太明显。当观众察觉到名次是被设计好的，竞技的意义就消失了。",
          "近两年口碑较好的作品，普遍的共同点是规则简单、评判透明，输赢不容易被怀疑。",
        ],
      },
      {
        heading: "强度上来了，人物反而更立得住",
        paragraphs: [
          "高强度竞技的优点在于，它会自然暴露一个人的性格：落后时的选择、团队里的角色、赢下比赛之后的反应，都很难伪装。",
          "这比在休息室里聊天更能让观众认识一个人，也更容易产生共情。",
        ],
      },
      {
        heading: "娱乐性应该来自过程",
        paragraphs: [
          "运动综艺并不需要放弃娱乐性。有趣的地方可以来自临场反应和意外，而不是来自剧本。",
          "当制作方愿意接受“结果不可控”，节目反而拥有了更强的生命力。",
        ],
      },
    ],
  },
  {
    id: "a12",
    slug: "niche-sport-short-video-route",
    title: "一个冷门项目如何靠短视频出圈：传播链路拆解",
    categoryId: "cat-game",
    excerpt:
      "没有转播资源、没有明星选手，一个冷门运动项目靠三段短视频完成了破圈。这条链路比想象中更可复制。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20for%20an%20entertainment%20news%20portal%2C%20muted%20sage%20green%20and%20vermilion%20red%20shapes%2C%20cream%20paper%20background%2C%20layered%20collage%20of%20connected%20nodes%20and%20play%20triangles%20suggesting%20viral%20short%20video%20network%2C%20subtle%20grain%20texture%2C%20flat%20minimal%20magazine%20cover%20aesthetic%2C%20no%20text%20no%20faces&width=1200&height=800&seq=kanliao-cover-12&orientation=landscape",
    tags: ["短视频", "破圈", "冷门项目"],
    heat: 4360,
    publishedAt: "2026-08-31",
    author: "编辑部 · 何一",
    readTime: 5,
    content: [
      {
        heading: "第一段视频解决“这是什么”",
        paragraphs: [
          "冷门项目出圈的第一道门槛是理解成本。第一条爆款视频没有讲规则，而是用十五秒展示了一个普通人也能看懂的瞬间。",
          "先把好奇心建立起来，规则可以留到后面再讲。这是短视频传播和传统转播最大的区别。",
        ],
      },
      {
        heading: "第二段视频解决“为什么好看”",
        paragraphs: [
          "有了初步关注之后，第二条内容开始展示技术含量：慢镜头、角度变化、选手的临场判断。它把“热闹”变成了“门道”。",
          "这一步很关键，它决定了观众是看一次就走，还是会持续关注。",
        ],
      },
      {
        heading: "第三段视频解决“我也能参与”",
        paragraphs: [
          "真正留住人的，是参与感。第三条内容是关于入门门槛的科普：需要什么装备、去哪能体验、大概花多少钱。",
          "从围观到参与，这一步走通，一个冷门项目才算真正拥有了稳定的人群。",
        ],
      },
    ],
  },
];